import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { XMarkIcon, ArrowUturnLeftIcon, ClockIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { useClickOutsideBlock } from "../../helpers/useClickOutside";
import { LoaderSpinner } from "../LoaderSpinner";
import {
    DemandPlanningLoadingKeys,
    getSkuAllocationChangeLog,
    undoLastSkuAllocationBatch,
    restoreSkuAllocationToTimestamp,
} from "../../slices/demandPlanningSlice";

const SOURCE_LABELS = {
    manual: "Manual edit",
    bulk_reset: "Bulk reset",
    ai_rebalance: "AI rebalance",
    rollback: "Rollback",
    kip: "Kip (approved)",
};

const formatChangedAt = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

export const SkuAllocationUndoRestoreModal = ({
    isOpen,
    onClose,
    demandPlanningId,
    demandPlanningYear,
    onApplied,
}) => {
    const dispatch = useDispatch();
    const modalRef = useRef(null);
    useClickOutsideBlock(modalRef, onClose);

    const [restoreTo, setRestoreTo] = useState("");
    const [latestBatch, setLatestBatch] = useState(null);
    const [isLoadingLatest, setIsLoadingLatest] = useState(false);
    const [confirmMode, setConfirmMode] = useState(null);

    const loadingKeys = useSelector((state) => state.demandPlanning.loadingKeys);
    const isUndoing = loadingKeys.includes(DemandPlanningLoadingKeys.undoLastSkuAllocationBatch);
    const isRestoring = loadingKeys.includes(
        DemandPlanningLoadingKeys.restoreSkuAllocationToTimestamp
    );
    const isBusy = isUndoing || isRestoring;

    useEffect(() => {
        if (!isOpen || demandPlanningId == null || demandPlanningYear == null) return;
        let cancelled = false;
        setIsLoadingLatest(true);
        setConfirmMode(null);
        dispatch(
            getSkuAllocationChangeLog({
                demandPlanningId,
                demandPlanningYear,
                limit: 50,
            })
        )
            .unwrap()
            .then((data) => {
                if (cancelled) return;
                const items = data?.items || [];
                if (!items.length) {
                    setLatestBatch(null);
                    return;
                }
                const latestBatchId = items[0].batchId;
                const entries = items.filter((item) => item.batchId === latestBatchId);
                setLatestBatch({
                    batchId: latestBatchId,
                    source: items[0].source,
                    changedAt: items[0].changedAt,
                    changedBy: items[0].changedBy,
                    count: entries.length,
                    skus: [...new Set(entries.map((entry) => entry.sku).filter(Boolean))],
                });
            })
            .catch(() => {
                if (!cancelled) setLatestBatch(null);
            })
            .finally(() => {
                if (!cancelled) setIsLoadingLatest(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, demandPlanningId, demandPlanningYear, dispatch]);

    const maxDateTime = useMemo(() => {
        const now = new Date();
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
        return now.toISOString().slice(0, 16);
    }, []);

    if (!isOpen) return null;

    const handleUndoLast = async () => {
        try {
            const result = await dispatch(
                undoLastSkuAllocationBatch({ demandPlanningId, demandPlanningYear })
            ).unwrap();
            toast.success(
                `Undid last change (${result?.rolledBackCount ?? 0} value${(result?.rolledBackCount ?? 0) === 1 ? "" : "s"} restored).`,
                { position: "bottom-right" }
            );
            onClose?.();
            await onApplied?.();
        } catch (err) {
            const msg = typeof err === "string" ? err : err?.message || "Could not undo the last change.";
            toast.error(msg, { position: "bottom-right" });
        } finally {
            setConfirmMode(null);
        }
    };

    const handleRestore = async () => {
        if (!restoreTo) {
            toast.error("Pick a date and time to restore to.", { position: "bottom-right" });
            setConfirmMode(null);
            return;
        }
        try {
            const result = await dispatch(
                restoreSkuAllocationToTimestamp({
                    demandPlanningId,
                    demandPlanningYear,
                    restoreTo: new Date(restoreTo).toISOString(),
                })
            ).unwrap();
            if (!result?.rolledBackCount) {
                toast.info("No changes after the selected time — nothing to restore.", {
                    position: "bottom-right",
                });
            } else {
                toast.success(
                    `Restored ${result.rolledBackCount} value${result.rolledBackCount === 1 ? "" : "s"} to how they were at the selected time.`,
                    { position: "bottom-right" }
                );
                onClose?.();
                await onApplied?.();
            }
        } catch (err) {
            const msg = typeof err === "string" ? err : err?.message || "Could not restore to the selected time.";
            toast.error(msg, { position: "bottom-right" });
        } finally {
            setConfirmMode(null);
        }
    };

    return (
        <div
            aria-hidden="true"
            className="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-[60] justify-center items-center w-full md:inset-0 h-[calc(100%)] max-h-full"
        >
            <div className="bg-gray-700 top-0 right-0 left-0 fixed z-[60] justify-center items-center w-full md:inset-0 h-[calc(100%)] max-h-full opacity-30" />
            <div
                ref={modalRef}
                className="relative p-5 w-full max-w-xl max-h-full bg-white border top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] rounded-md"
            >
                <div className="flex items-start justify-between">
                    <div>
                        <h4 className="text-lg font-semibold text-gray-900">Undo / Restore</h4>
                        <p className="mt-1 text-sm text-gray-500">
                            Revert SKU Allocation inputs using the change log. Every undo or restore is
                            itself logged, so it can be reversed too.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-primary hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>

                <div className="mt-5 flex flex-col gap-4">
                    <section className="rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center gap-2">
                            <ArrowUturnLeftIcon className="h-5 w-5 text-gray-500" />
                            <h5 className="text-sm font-semibold text-gray-900">Undo last change</h5>
                        </div>
                        {isLoadingLatest ? (
                            <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
                                <LoaderSpinner width="16" height="16" border="2" colorSpinner="gray" />
                                Loading latest change…
                            </div>
                        ) : latestBatch ? (
                            <p className="mt-2 text-sm text-gray-600">
                                Last change: {SOURCE_LABELS[latestBatch.source] || latestBatch.source}
                                {" — "}
                                {latestBatch.count} value{latestBatch.count === 1 ? "" : "s"}
                                {latestBatch.skus.length
                                    ? ` (${latestBatch.skus.slice(0, 3).join(", ")}${latestBatch.skus.length > 3 ? `, +${latestBatch.skus.length - 3} more` : ""})`
                                    : ""}
                                {latestBatch.changedBy ? ` by ${latestBatch.changedBy}` : ""}
                                {latestBatch.changedAt ? ` on ${formatChangedAt(latestBatch.changedAt)}` : ""}
                                .
                            </p>
                        ) : (
                            <p className="mt-2 text-sm text-gray-500">No logged changes to undo yet.</p>
                        )}
                        <div className="mt-3">
                            {confirmMode === "undo" ? (
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-700">Undo this change?</span>
                                    <button
                                        onClick={handleUndoLast}
                                        disabled={isBusy}
                                        className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                                    >
                                        {isUndoing ? (
                                            <LoaderSpinner width="16" height="16" border="2" colorSpinner="gray" />
                                        ) : (
                                            "Yes, undo"
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setConfirmMode(null)}
                                        disabled={isBusy}
                                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setConfirmMode("undo")}
                                    disabled={isBusy || isLoadingLatest || !latestBatch}
                                    className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                                >
                                    Undo last change
                                </button>
                            )}
                        </div>
                    </section>

                    <section className="rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center gap-2">
                            <ClockIcon className="h-5 w-5 text-gray-500" />
                            <h5 className="text-sm font-semibold text-gray-900">Restore from a date</h5>
                        </div>
                        <p className="mt-2 text-sm text-gray-600">
                            Restore all Override %, New Product Override %, and approval values to exactly
                            how they were at the selected date and time.
                        </p>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <input
                                type="datetime-local"
                                value={restoreTo}
                                max={maxDateTime}
                                onChange={(event) => {
                                    setRestoreTo(event.target.value);
                                    setConfirmMode(null);
                                }}
                                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                            {confirmMode === "restore" ? (
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-700">Restore to this time?</span>
                                    <button
                                        onClick={handleRestore}
                                        disabled={isBusy}
                                        className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                                    >
                                        {isRestoring ? (
                                            <LoaderSpinner width="16" height="16" border="2" colorSpinner="gray" />
                                        ) : (
                                            "Yes, restore"
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setConfirmMode(null)}
                                        disabled={isBusy}
                                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setConfirmMode("restore")}
                                    disabled={isBusy || !restoreTo}
                                    className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                                >
                                    Restore
                                </button>
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default SkuAllocationUndoRestoreModal;
